import { type IReturn } from "@servicestack/client";
import { useMemo } from "react";
import { type KeyedMutator, type SWRConfiguration, type SWRResponse } from "swr";
import useSwrClient, { type IHttpVerb } from "./useSwrClient";

export interface IApiConfiguration<T, D, E, A> {
    getData?: (response?: SWRResponse<T, T>) => D | undefined;
    getError?: (response?: SWRResponse<T, T>) => E | undefined;
    getAdditionalState?: (response?: SWRResponse<T, T>) => A | undefined;
}

export const useApi = <T, D, E, A>(
    httpVerb: IHttpVerb,
    fn?: () => IReturn<T>,
    getConfiguration?: IApiConfiguration<T, D, E, A>,
    swrConfiguration?: SWRConfiguration | undefined,
) => {
    const { api } = useSwrClient();
    const response = api<T>(httpVerb, fn, swrConfiguration);

    const { getData, getError, getAdditionalState } = getConfiguration ?? {};

    const result: {
        data: D | undefined;
        error: E | undefined;
        loading: boolean;
        validating: boolean;
        mutate: KeyedMutator<T>;
        refresh: () => Promise<T>;
    } & A = useMemo(() => {
        const d = getData?.(response);
        const e = getError?.(response);
        const a = getAdditionalState?.(response);

        const s = {
            data: d,
            error: e,
            loading: response.isValidating && !d && !e,
            validating: response.isValidating,
            mutate: response.mutate,
            refresh: async () => {
                const newResponse = await response.mutate(response.data, { revalidate: true });

                return newResponse;
            },
        };

        return {
            ...s,
            ...a,
        } as typeof s & A;
    }, [
        getData,
        getError,
        response.data,
        response.error,
        response.isLoading,
        response.isValidating,
        response.mutate,
    ]);

    return result;
};

export default useApi;
